import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Iconify from '../../../../components/iconify';
import { Divider, SaveButton, SkillkoFieldBadge, EditedText } from '../components';
import { isSkillkoSourcedEdit } from '../profileFormConfig';

/**
 * Employee Details section of the profile form
 * Handles: company, role
 */
const EmployeeDetailsSection = ({
  mode,
  config,
  profileData,
  initialProfileData,
  metadata,
  errors,
  handleInputChange,
  checkFieldDisabled,
  handleAddNewCompany,
  handleAddNewRole,
  onSave,
  isEdited,
  canEdit,
}) => {
  const { sectionSave } = config;
  const isSkillkoEdit = isSkillkoSourcedEdit(mode, profileData);
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const gridSpacing = isSmUp ? 3 : 2;

  const isCompanyCreated = (value) => !!metadata.companies?.find(x => x.name === value);
  const isRoleCreated = (value) => !!metadata.roles?.find(x => x.name?.toLowerCase() === value?.toLowerCase());

  // Generate stable keys - only changes when disabled state changes
  const getFieldKey = (fieldName, disabled) => {
    return `${fieldName}-${!!disabled}`;
  };

  // Render company field
  const renderCompanyField = () => (
    <Autocomplete
      key={getFieldKey('company', checkFieldDisabled('company'))}
      options={metadata.companies}
      getOptionLabel={(option) => option.name}
      value={profileData.company || null}
      data-testid="company-autocomplete"
      onChange={(event, newValue) => handleInputChange('company', newValue)}
      disabled={checkFieldDisabled('company')}
      renderInput={(params) => (
        <TextField
          {...params}
          name="company"
          inputProps={{
            ...params.inputProps,
            name: 'company',
            'aria-label': 'company',
          }}
          label="Company"
          error={!!errors.company}
          helperText={errors.company || ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {canEdit && params.inputProps.value.length > 3 && !isCompanyCreated(params.inputProps.value) && (
                  <IconButton
                    color="primary"
                    onClick={() => handleAddNewCompany(params.inputProps.value)}
                  >
                    <Iconify icon="eva:plus-fill" width={25} />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  // Render role field
  const renderRoleField = () => (
    <Autocomplete
      key={getFieldKey('role', checkFieldDisabled('role'))}
      options={metadata.roles || []}
      getOptionLabel={(option) => option.name || ''}
      value={profileData.role || null}
      onChange={(event, newValue) => handleInputChange('role', newValue)}
      disabled={checkFieldDisabled('role')}
      renderInput={(params) => (
        <TextField
          {...params}
          name="role"
          inputProps={{
            ...params.inputProps,
            name: 'role',
            'aria-label': 'role',
          }}
          label="Role"
          error={!!errors.role}
          helperText={errors.role || ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {canEdit && params.inputProps.value.length > 3 && !isRoleCreated(params.inputProps.value) && (
                  <IconButton
                    color="primary"
                    onClick={() => handleAddNewRole(params.inputProps.value)}
                  >
                    <Iconify icon="eva:plus-fill" width={25} />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  return (
    <>
      <Grid item xs={12} marginBottom={{ xs: 2, sm: 3 }} marginTop={{ xs: 2, sm: 3 }}>
        <Divider text="Employee Details" />
      </Grid>

      <Grid container spacing={gridSpacing}>
        {/* Company */}
        {sectionSave ? (
          <>
            <Grid item sm={6} xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box data-profile-field="company" sx={{ flex: 1, minWidth: 0 }}>
                  {renderCompanyField()}
                </Box>
                {isSkillkoEdit && (
                  <Box sx={{ mt: 2 }}>
                    <SkillkoFieldBadge />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item sm={6} xs={3}>
              {initialProfileData.company !== profileData.company && (
                <EditedText />
              )}
            </Grid>
          </>
        ) : (
          <>
            <Grid item sm={8} xs={12}>
              <Box data-profile-field="company">
                {renderCompanyField()}
              </Box>
            </Grid>
            <Grid item sm={4} xs={0}></Grid>
          </>
        )}

        {/* Role */}
        {sectionSave ? (
          <>
            <Grid item sm={6} xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box data-profile-field="role" sx={{ flex: 1, minWidth: 0 }}>
                  {renderRoleField()}
                </Box>
                {isSkillkoEdit && (
                  <Box sx={{ mt: 2 }}>
                    <SkillkoFieldBadge />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item container sm={6} xs={3} justifyContent="space-between">
              <Grid item>
                {initialProfileData.role !== profileData.role && (
                  <EditedText />
                )}
              </Grid>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <SaveButton 
                  disabled={!isEdited} 
                  onClick={onSave}
                  sectionSave={sectionSave}
                  canEdit={canEdit}
                />
              </Box>
            </Grid>
            <Grid 
              item 
              container 
              xs={12} 
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              sx={{ mt: { xs: 1, sm: 0 } }}
            >
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <SaveButton 
                  disabled={!isEdited} 
                  onClick={onSave}
                  sectionSave={sectionSave}
                  canEdit={canEdit}
                />
              </Box>
            </Grid>
          </>
        ) : (
          <Grid item sm={8} xs={12}>
            <Box data-profile-field="role">
              {renderRoleField()}
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default EmployeeDetailsSection;
